/**
 * Uses Swiper package to create a carousel for product media
 * More info: https://swiperjs.com/
 */
class ProductMedia extends HTMLElement {
  constructor() {
    super();

    this.selectors = {
      slider: '[data-slider]',
      thumbs: '[data-thumbs]',
      realSlide: '[data-slider] .swiper-slide[data-media-id]',
      pagination: '.swiper-pagination',
      paginationBullet: '.swiper-pagination-bullet',
    };
    this.realSlideCount = Number(this.dataset.realSlideCount) || 0;
    this.bookendOffset = this.realSlideCount > 1 ? 2 : 0;
    this.selectedMediaIndex =
      Number(
        this.querySelector(this.selectors.slider)?.querySelector(
          '[data-selected]',
        )?.dataset.index,
      ) || 0;
    this.settings = {
      elements: {
        slider: this.querySelector(this.selectors.slider),
        thumbs: this.querySelector(this.selectors.thumbs),
        pagination: this.querySelector(this.selectors.pagination),
      },
      instances: {
        slider: null,
        thumbs: null,
      },
      options: {
        slider: {
          loop: false,
          initialSlide: this.selectedMediaIndex + this.bookendOffset,
          spaceBetween: 16,
          watchOverflow: true,
          navigation: {
            prevEl: this.querySelector('.swiper-arrow--prev'),
            nextEl: this.querySelector('.swiper-arrow--next'),
          },
          breakpoints: {
            990: {
              spaceBetween: 24,
            },
          },
        },
        thumbs: {
          initialSlide: this.selectedMediaIndex,
          slidesPerView: 5,
          spaceBetween: 16,
        },
      },
    };
  }

  connectedCallback() {
    this.init();
  }

  init() {
    if (!this.settings.elements.slider || !this.settings.elements.thumbs)
      return;

    this.buildPagination();

    this.settings.instances.thumbs = new Swiper(
      this.settings.elements.thumbs,
      this.settings.options.thumbs,
    );

    this.settings.instances.slider = new Swiper(
      this.settings.elements.slider,
      this.settings.options.slider,
    );

    this.settings.instances.slider.on('slideChangeTransitionStart', () => {
      this.syncThumbs();
      this.updatePaginationActive();
    });

    this.settings.instances.slider.on('transitionEnd', () => {
      this.fixBookends();
    });

    this.settings.instances.thumbs.on('click', (swiper) => {
      if (swiper.clickedIndex === undefined) return;
      this.slideToRealIndex(swiper.clickedIndex);
    });

    this.syncThumbs();
    this.updatePaginationActive();

    requestAnimationFrame(() => {
      const productSection = this.closest('.product');

      productSection.style.overflow = 'initial';
      productSection.style.overflowX = 'clip';
    });
  }

  buildPagination() {
    const pagination = this.settings.elements.pagination;
    if (!pagination) return;

    pagination.innerHTML = '';

    for (let i = 0; i < this.realSlideCount; i += 1) {
      const bullet = document.createElement('button');
      bullet.type = 'button';
      bullet.className = 'swiper-pagination-bullet';
      bullet.dataset.realIndex = String(i);
      bullet.addEventListener('click', () => this.slideToRealIndex(i));
      pagination.appendChild(bullet);
    }
  }

  getRealIndex() {
    return this.settings.instances.slider.activeIndex - this.bookendOffset;
  }

  slideToRealIndex(realIndex) {
    this.settings.instances.slider.slideTo(realIndex + this.bookendOffset);
  }

  syncThumbs() {
    const realIndex = this.getRealIndex();
    if (realIndex < 0 || realIndex >= this.realSlideCount) return;
    this.settings.instances.thumbs.slideTo(realIndex);
  }

  updatePaginationActive() {
    const realIndex = this.getRealIndex();
    if (realIndex < 0 || realIndex >= this.realSlideCount) return;

    this.querySelectorAll(this.selectors.paginationBullet).forEach(
      (bullet, index) => {
        bullet.classList.toggle(
          'swiper-pagination-bullet-active',
          index === realIndex,
        );
      },
    );
  }

  fixBookends() {
    const slider = this.settings.instances.slider;
    const trailingCloneIndex = this.realSlideCount + this.bookendOffset;

    if (slider.activeIndex === 0) {
      slider.slideTo(this.realSlideCount, 0, false);
      return;
    }

    if (slider.activeIndex === trailingCloneIndex) {
      slider.slideTo(this.bookendOffset, 0, false);
    }
  }

  setActiveMedia(id) {
    const mediaFound = this.querySelector(
      `[data-slider] .swiper-slide[data-media-id="${id}"]`,
    );
    if (!mediaFound) return;
    this.slideToRealIndex(Number(mediaFound.dataset.index));
  }

  replaceContent(html, slideCount) {
    this.settings.instances.slider?.destroy(true, true);
    this.settings.instances.thumbs?.destroy(true, true);

    this.innerHTML = html;
    this.dataset.realSlideCount = slideCount;

    this.realSlideCount = Number(slideCount) || 0;
    this.bookendOffset = this.realSlideCount > 1 ? 2 : 0;
    this.selectedMediaIndex = 0;
    this.settings.elements.slider = this.querySelector(this.selectors.slider);
    this.settings.elements.thumbs = this.querySelector(this.selectors.thumbs);
    this.settings.elements.pagination = this.querySelector(
      this.selectors.pagination,
    );
    this.settings.options.slider.initialSlide = this.bookendOffset;
    this.settings.options.thumbs.initialSlide = 0;
    this.settings.options.slider.navigation.prevEl = this.querySelector(
      '.swiper-arrow--prev',
    );
    this.settings.options.slider.navigation.nextEl = this.querySelector(
      '.swiper-arrow--next',
    );

    this.init();
  }
}

customElements.define('product-media', ProductMedia);
